import { AxiosResponse } from 'axios';
import { SET_SCREENS, SET_SCREEN, SET_ERRORMSG } from '@/store/mutation_types';
import {
  CLEAR_SCREEN_ISLOADING,
  DELETE_SCREEN,
  EDIT_SCREEN,
  GET_SCREEN,
  GET_SCREENS,
  GET_SCREENS_UNPOPULATED,
  REFRESH_SCREEN,
  REGISTER_SCREEN,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import Screen from '@/services/screen';
import { Module } from 'vuex';
import { EditScreen, RegisterScreen, GetScreen } from '@/dtos/screen.dto';

// What GET_SCREEN resolves to when a newer GET_SCREEN overtook it: nothing was
// committed, and the caller should act as if the request never happened.
export const SCREEN_REQUEST_STALE = 'stale';
let latestScreenRequestId = 0;

const screen: Module<any, {}> = {
  state: {
    screen: {
      data: null,
      isLoading: false,
    },
    screens: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    screen: (state: { screen: any }) => state.screen,
    screens: (state: { screens: any }) => state.screens,
  },
  mutations: {
    [SET_SCREEN]: (
      state: { screen: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.screen.data = payload.data) : null,
        (state.screen.isLoading = payload.isLoading);
    },
    [SET_SCREENS]: (
      state: { screens: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.screens.data = payload.data) : null,
        (state.screens.isLoading = payload.isLoading);
    },
  },
  actions: {
    [REGISTER_SCREEN]: async ({ commit }: any, payload: FormData) => {
      commit(SET_SCREEN, { data: null, isLoading: true });

      const result: AxiosResponse = await Screen.registerScreen(
        payload
      );

      if (result) {
        if (result.data.status) {
          commit(SET_SCREEN, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SCREEN, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SCREEN, { isLoading: false });
        return false;
      }
    },
    [GET_SCREENS]: async ({ commit }: any, payload: any) => {
      commit(SET_SCREENS, { data: null, isLoading: true });

      const result: AxiosResponse = await Screen.getScreens(
        payload as GetScreen
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SCREENS, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_SCREENS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SCREENS, { isLoading: false });
        return false;
      }
    },
    [GET_SCREENS_UNPOPULATED]: async ({ commit }: any, payload: any) => {
      commit(SET_SCREENS, { data: null, isLoading: true });

      const result: AxiosResponse = await Screen.getScreensUnpopulated(
        payload as GetScreen
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SCREENS, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_SCREENS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SCREENS, { isLoading: false });
        return false;
      }
    },
    [REFRESH_SCREEN]: async ({ commit }: any, payload: any) => {
      commit(SET_SCREEN, { data: null, isLoading: true });

      const result: AxiosResponse = await Screen.refreshScreen(
        payload as GetScreen
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SCREEN, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_SCREEN, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SCREEN, { isLoading: false });
        return false;
      }
    },
    [GET_SCREEN]: async ({ commit }: any, payload: any) => {
      // Only the newest request may land. A schedule link switches the one
      // display between screens, so a slow answer for the screen it just left
      // must not overwrite the one it switched to — nor clear its loading flag.
      const requestId = ++latestScreenRequestId;
      commit(SET_SCREEN, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Screen.getScreens(payload as GetScreen);
      } catch (error) {
        // A dropped connection rejects rather than resolving empty. Settle the
        // loading flag either way, or a display that lost its network during a
        // refresh would sit behind its loading overlay until the next one.
        if (requestId !== latestScreenRequestId) return SCREEN_REQUEST_STALE;
        commit(SET_SCREEN, { isLoading: false });
        return false;
      }

      if (requestId !== latestScreenRequestId) return SCREEN_REQUEST_STALE;

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SCREEN, { data: result.data.data[0], isLoading: false });
          return true;
        }
        commit(SET_SCREEN, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SCREEN, { isLoading: false });
        return false;
      }
    },
    [EDIT_SCREEN]: async ({ commit }: any, payload: FormData) => {
      commit(SET_SCREEN, { data: null, isLoading: true });

      const result: AxiosResponse = await Screen.editScreen(
        payload
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SCREEN, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SCREEN, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SCREEN, { isLoading: false });
        return false;
      }
    },
    [DELETE_SCREEN]: async ({ commit }: any, payload: string) => {
      commit(SET_SCREEN, { data: null, isLoading: true });

      const result: AxiosResponse = await Screen.deleteScreen(payload);

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_SCREEN, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_SCREEN, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_SCREEN, { isLoading: false });
        return false;
      }
    },
    [CLEAR_SCREEN_ISLOADING]: ({ commit }) => {
      commit(SET_SCREEN, { isLoading: false });
      commit(SET_SCREENS, { isLoading: false });
    },
  },
};

export default screen;
